﻿import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { IndexedDbService } from './indexed-db.service';
import { from, switchMap } from 'rxjs';

import { environment } from '../../environments/environment';
import { User } from '../_models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private userSubject: BehaviorSubject<User | null>;
    public user: Observable<User | null>;

    private client_id = `${environment.client_id}`;
    private redirect_uri = `${environment.redirectUrl}`;
    private scope = `${environment.client_id} offline_access`;
    private enviroment_url = `${environment.apiBaseUrl}`;
    private token_endpoint = `${this.enviroment_url}/b2c_1a_signup_signin/oauth2/v2.0/token`;
    private response_type = 'code';
    private response_mode = 'query';
    private prompt = 'login';
    public defaultCode: string = '';
    public user$: Observable<User | null>;
    private tokenEndpoint = `${environment.apiBaseUrl}/b2c_1a_signup_signin/oauth2/v2.0/token`;

    constructor(
        private router: Router,
        private db: IndexedDbService,
        private http: HttpClient
    ) {
        this.userSubject = new BehaviorSubject(
            JSON.parse(sessionStorage.getItem('user')!)
        );
        this.user$ = this.userSubject.asObservable();
    }

    public get userValue(): User | null {
        return this.userSubject.value;
    }

    async getAccessToken(): Promise<string | null> {
        return this.db.getData('access_token');
    }
    
    logout() {
        sessionStorage.clear();
        this.db.clear();
        this.userSubject.next(null);
        // Notifica outras abas para fazer logout
        localStorage.setItem('logout_broadcast', 'true');
        localStorage.removeItem('logout_broadcast');
        this.router.navigate(['/login']);
    }

    refreshTokenRequest() {
        return from(this.db.getData('refresh_token')).pipe(
            switchMap(refresh_token => {
                // If there's no refresh token, we can't refresh.
                // Log out the user and throw an error to stop the chain.
                if (!refresh_token) {
                    this.logout();
                    return throwError(() => new Error('Refresh token not found. User logged out.'));
                }
                const formData = new FormData();
                formData.append('client_id', environment.client_id);
                formData.append('redirect_uri', environment.redirectUrl);
                formData.append('scope', 'offline_access openid');
                formData.append('refresh_token', refresh_token);
                formData.append('grant_type', 'refresh_token');

                return this.http.post<any>(this.tokenEndpoint, formData); // ⬅️ http.post já retorna Observable
            })
        );
    }

    // ================================

    getLoginUrl(code_challenge: string): string {
        const nonce = this.generateCodeVerifier(32);
        const state = this.generateCodeVerifier(32);
        sessionStorage.setItem('oauth_nonce', nonce);
        sessionStorage.setItem('oauth_state', state);

        return `${environment.apiBaseUrl}/b2c_1a_signup_signin/oauth2/v2.0/authorize` +
            `?client_id=${this.client_id}` +
            `&scope=${encodeURIComponent(this.scope)}` +
            `&redirect_uri=${encodeURIComponent(this.redirect_uri)}` +
            `&response_type=${this.response_type}` +
            `&response_mode=${this.response_mode}` +
            `&prompt=${this.prompt}` +
            `&nonce=${nonce}` +
            `&state=${state}` +
            `&code_challenge=${code_challenge}` +
            `&code_challenge_method=S256`;
    }
    generateCodeVerifier(length: number = 64): string {
        const array = new Uint8Array(length);
        window.crypto.getRandomValues(array);
        return this.base64UrlEncode(array);
    }

    async generateCodeChallenge(codeVerifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        return this.base64UrlEncode(new Uint8Array(digest));
    }

    private base64UrlEncode(array: Uint8Array): string {
        return btoa(String.fromCharCode(...array))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    exchangeToken(code: string, code_verifier: string) {
        const formData = new FormData();
        formData.append('client_id', this.client_id);
        formData.append('redirect_uri', this.redirect_uri);
        formData.append('scope', 'offline_access openid');
        formData.append('code', code);
        formData.append('code_verifier', code_verifier);
        formData.append('grant_type', 'authorization_code');
        formData.append('client_info', '1');

        return this.http.post(this.token_endpoint, formData);
    }
}